import { clsx } from "clsx";
import type { MouseEvent } from "react";
import {
  IconCheck,
  IconCircle,
  IconCircleCheckFilled,
  IconHeart,
  IconHeartFilled,
  IconShoppingCart,
} from "@tabler/icons-react";
import type { ProductDto } from "../../../../api";
import { Image } from "../../../../components/common/image";
import { Button } from "../../../../components/common/button";
import { Chip } from "../../../../components/common/chip";
import { Text } from "../../../../components/common/typography";
import { GatedPrice } from "../../../../components/common/gated-price";
import { useCompareUiStore } from "../../../../hooks/stores/useCompareUiStore";
import { useWishlistUiStore } from "../../../../hooks/stores/useWishlistUiStore";
import { getConditionLabel } from "../../utils/condition-label";

export interface ProductCardProps {
  product: ProductDto;
  discountLabel?: string;
  loyaltyLabel?: string;
  originalPrice?: number;
  stockLabel?: string;
  currency?: string;
  selectable?: boolean;
  selected?: boolean;
  onToggleSelect?: () => void;
  compareMode?: boolean;
  compareDisabled?: boolean;
  onViewDetails?: () => void;
  onAddToCart?: (e: MouseEvent<HTMLButtonElement>) => void;
  isInWishlist?: boolean;
  onToggleWishlist?: (e: MouseEvent) => void;
}

export function ProductCard({
  product,
  discountLabel,
  loyaltyLabel,
  originalPrice,
  stockLabel,
  currency = "QAR",
  selectable = false,
  selected = false,
  onToggleSelect,
  compareMode = false,
  compareDisabled = false,
  onViewDetails,
  onAddToCart,
  isInWishlist = false,
  onToggleWishlist,
}: ProductCardProps) {
  const playFlyToCompare = useCompareUiStore((s) => s.playFlyToCompare);
  const playFlyToWishlist = useWishlistUiStore((s) => s.playFlyToWishlist);

  const handleCompareClick = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    const wasSelected = selected;
    onToggleSelect?.();
    if (!wasSelected) {
      playFlyToCompare({
        source: e.currentTarget,
        image: product.image ?? undefined,
      });
    }
  };

  const title = product.name || "—";
  const description = product.description;
  const warranty = product.warranty?.name;
  const price = product.listPrice ?? 0;
  const conditionLabel = getConditionLabel(product.conditions);

  return (
    <article
      className={clsx(
        "group relative flex h-full w-full flex-col gap-4 rounded-2xl border bg-bgWhite px-6 py-3.5",
        "shadow-[0px_2px_6px_0px_rgba(190,189,217,0.2)] transition-colors",
        selected ? "border-brandPrimary" : "border-borderDefault",
      )}
    >
      {/* Wishlist button — top-left overlay */}
      {onToggleWishlist && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            if (!isInWishlist) {
              playFlyToWishlist({
                source: e.currentTarget,
                image: product.image ?? undefined,
              });
            }
            onToggleWishlist(e);
          }}
          aria-label={isInWishlist ? "Remove from wishlist" : "Add to wishlist"}
          className="absolute left-6 top-6 z-10 flex size-9 items-center justify-center rounded-full bg-bgLight transition-colors hover:bg-bgMuted"
        >
          {isInWishlist
            ? <IconHeartFilled size={18} className="text-textBody" />
            : <IconHeart size={18} className="text-textMuted" />}
        </button>
      )}

      {/* Select (compare) toggle — top-right pill */}
      {selectable ? (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onToggleSelect?.();
          }}
          aria-pressed={selected}
          disabled={!selected && compareDisabled}
          className={clsx(
            "absolute right-6 top-6 z-10 inline-flex items-center gap-2 rounded-[10px] border bg-bgWhite px-3 py-1.5 transition-colors",
            selected
              ? "border-brandPrimary"
              : "border-borderDefault hover:border-borderDark",
            !selected && compareDisabled && "cursor-not-allowed opacity-50",
          )}
        >
          {selected ? (
            <IconCircleCheckFilled size={20} className="text-brandPrimary" />
          ) : (
            <IconCircle size={16} className="text-borderDark" />
          )}
          <Text variant="bodySm" tone="dark" inline>
            Select
          </Text>
        </button>
      ) : null}

      {/* Product image */}
      <div className="relative aspect-[207/141] w-full shrink-0 overflow-hidden rounded-lg">
        <Image
          image={product.image}
          alt={title}
          size="w-full h-full"
          objectFit="contain"
          showPlaceholder
        />
      </div>

      {/* Product info */}
      <div className="flex w-full flex-1 flex-col gap-1">
        {/* Name + discount chip */}
        <div className="flex w-full items-start justify-between gap-2">
          <Text variant="bodyBold" tone="dark" className="line-clamp-2 leading-[26px]">
            {title}
          </Text>
          {discountLabel || loyaltyLabel || conditionLabel ? (
            <span className="flex shrink-0 flex-col items-end gap-1">
              {conditionLabel ? <Chip variant="tertiary">{conditionLabel}</Chip> : null}
              {discountLabel ? <Chip variant="secondary">{discountLabel}</Chip> : null}
              {loyaltyLabel ? <Chip variant="approved">{loyaltyLabel}</Chip> : null}
            </span>
          ) : null}
        </div>

        {/* Specs line */}
        {description ? (
          <Text variant="bodySm" tone="dark" className="line-clamp-1">
            {description}
          </Text>
        ) : null}

        {/* Warranty */}
        {warranty ? (
          <Text variant="bodySm" tone="dark" className="line-clamp-1">
            {warranty} Warranty
          </Text>
        ) : null}

        {/* Price + original price + stock label */}
        <div className="flex w-full items-end justify-between gap-2 pt-0.5">
          <div className="flex items-end gap-2">
            <Text variant="bodyBold" tone="dark" className="leading-[26px]">
              <GatedPrice>{currency} {price.toLocaleString()}</GatedPrice>
            </Text>
            {originalPrice != null && originalPrice > price ? (
              <Text variant="capsSm" tone="body" inline className="line-through pb-0.5">
                <GatedPrice>{currency} {originalPrice.toLocaleString()}</GatedPrice>
              </Text>
            ) : null}
          </div>
          {stockLabel ? (
            <Text variant="capsSm" inline className="text-[#946f01] uppercase whitespace-nowrap">
              {stockLabel}
            </Text>
          ) : null}
        </div>
      </div>

      {/* Action buttons */}
      <div className="mt-auto flex w-full flex-col gap-1.5">
        {compareMode ? (
          <Button
            variant={selected ? "secondary" : "primary"}
            fullWidth
            size="sm"
            disabled={!selected && compareDisabled}
            leftIcon={selected ? <IconCheck size={16} /> : <IconShoppingCart size={16} />}
            onClick={handleCompareClick}
          >
            {selected ? "Added to compare" : "Add to compare"}
          </Button>
        ) : (
          <>
            <Button
              variant="primary"
              fullWidth
              size="sm"
              leftIcon={onAddToCart ? <IconShoppingCart size={16} /> : undefined}
              onClick={(e) => {
                e.stopPropagation();
                if (onAddToCart) {
                  onAddToCart(e);
                } else {
                  onViewDetails?.();
                }
              }}
            >
              {onAddToCart ? "Add to cart" : "See options"}
            </Button>
            {onAddToCart && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onViewDetails?.();
                }}
                className="w-full rounded-xl px-2.5 py-2.5 text-center transition-colors hover:bg-bgLight"
              >
                <Text variant="button" tone="dark" inline className="underline">
                  View full details
                </Text>
              </button>
            )}
          </>
        )}
      </div>
    </article>
  );
}

export default ProductCard;
